import React from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleNavLauncher } from "./ModuleNavLauncher";
import { ModuleConfig } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";
import { useAuth } from "../contexts/AuthContext";

interface ModulePageLayoutProps {
  moduleConfig: ModuleConfig;
  children: React.ReactNode;
  /** Container maxW - defaults to "container.xl" */
  maxW?: string;
  /** Container py - defaults to 8 */
  py?: number;
  /** Skip Container wrapper entirely (for pages that manage their own layout) */
  noContainer?: boolean;
  /** Skip the module navigation launcher (kept as `noBreadcrumb` for call-site compatibility) */
  noBreadcrumb?: boolean;
  /** Skip footer (full-bleed canvas pages) */
  noFooter?: boolean;
  /** Pin the frame to exactly 100dvh + overflow:hidden — the page owns its own
      internal scroll regions (e.g. knowledgeexplorer's 3 panes). */
  fillViewport?: boolean;
}

/**
 * ModulePageLayout — Shared layout wrapper for module pages.
 *
 * Provides consistent page scaffolding:
 *   1. NavbarWithCallToAction (top)
 *   2. ModuleNavLauncher — the module's navigation as a draggable floating button
 *   3. Container wrapping page content (configurable maxW)
 *   4. FooterWithFourColumns (bottom, pushed down by flex)
 *
 * Usage: Set `useLayout: true` on routes in moduleConfig.ts.
 * App.tsx wraps those routes with this component automatically,
 * passing the RESOLVED moduleConfig (with real permissions from backend).
 *
 * Page components should return ONLY their content — no Navbar, Footer,
 * navigation, Container, or outer Box wrappers.
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  children,
  maxW = "container.xl",
  py = 8,
  noContainer = false,
  noBreadcrumb = false,
  noFooter = false,
  fillViewport = false,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const { isAuthenticated } = useAuth();

  return (
    <Box
      bg={bgMain}
      display="flex"
      flexDirection="column"
      {...(fillViewport ? { h: "100dvh", overflow: "hidden" } : { minHeight: "100vh" })}
    >
      <NavbarWithCallToAction />
      {/* Module navigation is a DRAGGABLE FLOATING LAUNCHER, not the sticky bar it used to be
          (vault/how-to/draggable-floating-launchers.md). The bar cost every module page a strip
          of screen whether or not the reader wanted to navigate; the launcher gives the same
          links back and gets out of the way. Still authenticated-only — a logged-out guest
          following a public link (e.g. /bill/:id) sees no module navigation at all.
          `noBreadcrumb` keeps its name so existing callers are unaffected. */}
      {!noBreadcrumb && isAuthenticated && <ModuleNavLauncher moduleConfig={moduleConfig} />}
      {noContainer ? (
        <Box
          flex="1"
          {...(fillViewport ? { minH: 0, overflow: "hidden", display: "flex", flexDirection: "column" } : {})}
        >
          {children}
        </Box>
      ) : (
        <Container maxW={maxW} py={py} flex="1">
          {children}
        </Container>
      )}
      {!noFooter && <FooterWithFourColumns />}
    </Box>
  );
};
